import instance from "./index";
import { requireAuthToken } from "../utils/authUtils";

const normalizeIdArray = (values) => {
  const list = Array.isArray(values) ? values : values == null ? [] : [values];

  return list
    .map((value) => Number(value))
    .filter((id) => Number.isFinite(id));
};

const normalizeNameArray = (values) => {
  const list = Array.isArray(values) ? values : values == null ? [] : [values];

  return [
    ...new Set(
      list
        .map((value) => String(value ?? "").trim())
        .filter(Boolean),
    ),
  ];
};

export const createPlamboUserProfile = async ({
  userId,
  globalRoles = [],
  jobTitles = [],
  userName = "",
  email = "",
  firstName = "",
  lastName = "",
  skills = [],
  expertise = [],
}) => {
  const trimmedUserId = String(userId ?? "").trim();
  const trimmedUserName = String(userName ?? "").trim();

  if (!trimmedUserId) {
    throw new Error("User id is required");
  }

  if (!trimmedUserName) {
    throw new Error("User name is required");
  }

  const payload = {
    userId: trimmedUserId,
    globalRoles: normalizeIdArray(globalRoles),
    jobTitles: normalizeIdArray(jobTitles),
    userName: trimmedUserName,
    email: String(email ?? "").trim(),
    firstName: String(firstName ?? "").trim(),
    lastName: String(lastName ?? "").trim(),
    skills: normalizeNameArray(skills),
    expertise: normalizeNameArray(expertise),
  };

  try {
    const response = await instance.post("/profile/createUser", payload);

    if (response.status !== 201) {
      throw new Error(
        `Profile creation failed with status ${response.status}. Expected 201.`,
      );
    }

    return response;
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.detail ||
      error?.message ||
      "Failed to create user profile";

    throw new Error(message);
  }
};

export const createNewUser = async function (payload){
  const  {data} = await instance.post("/profile/createNewUser", payload);
  return data;
}

const ensureUploadableProfileFile = (file) => {
  const isGif = file.type === "image/gif" || /\.gif$/i.test(file.name || "");

  if (isGif && (file.type !== "image/gif" || !/\.gif$/i.test(file.name || ""))) {
    const filename = /\.gif$/i.test(file.name || "") ? file.name : `${file.name || "profile"}.gif`;

    return new File([file], filename, {
      type: "image/gif",
      lastModified: file.lastModified,
    });
  }

  return file;
};

export const uploadProfile = async (file) => {
  if (!file) throw new Error("File is required");

  const uploadFile = ensureUploadableProfileFile(file);
  const formData = new FormData();
  formData.append("file", uploadFile, uploadFile.name);

  return await instance.post("/profile/upload", formData);
};

export const getProfile = async () => {
  return await instance.get("/profile/view", {
    responseType: "blob",
  });
};

export const getProfileByUserId = async (userId) => {
  if (!userId) throw new Error("User id is required");

  return await instance.get(`/profile/view/${userId}`, {
    responseType: "blob",
  });
};

export const deleteProfilePhoto = async () => {
  return await instance.delete("/profile");
};
// triggers an email when a user status is updated 
export const notifyAdminOnStatus = async (payload) => {
  return await instance.post("/profile/sendEmailToUser", payload);
};

export const getUsersByClientId = async (clientId) => {
  const trimmedClientId = String(clientId ?? "").trim();
  if (!trimmedClientId) return [];

  const token = requireAuthToken();
  const res = await instance.get(
    `/profile/getUsersByClientId/${encodeURIComponent(trimmedClientId)}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  const data = res?.data;
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
      ? data.data
      : [];

  return list.map((item) => String(item).trim()).filter(Boolean);
};

